import { useState } from 'react';
import { Table, Input, Button, Spin, message, Tag, Modal, Tooltip, Form, InputNumber, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  LockOutlined,
  SearchOutlined,
  UnlockOutlined,
  EyeOutlined,
  EditOutlined,
  WalletOutlined,
  SyncOutlined,
  IdcardOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useBlockUnblockUser, useFetchUsers, useEditCustomer, useAddCustomerCredit, useResetCustomerPin, useGetCustomerPin } from './Hooks';
import useDebounce from '../../utils/debounce';
import CustomerDrawer from '../../components/CreateDrawer';
import CustomerDrawerView from '../../components/CustomerDrawerView';
import EditCustomerDrawer from '../../components/EditCustomerDrawer';
import AddRFIDModalDirectlyUser from '../../components/Modals/AddRFIDModalDirectlyUser';
import { useIsEditAllowedWithOperator } from '../../utils/utils';
import { useOperatorStore } from '../../store/useOperatorStore';
import WarningMessage from '../../components/WarningMessage';
import { getErrorMessage } from '../../utils/errorHandler';

const CustomersListPage = () => {
  const { t } = useTranslation('users');
  const queryClient = useQueryClient();
  const showWallet = import.meta.env.VITE_SHOW_WALLET === 'true';
  const isEditAllowed = useIsEditAllowedWithOperator();
  const { operatorId } = useOperatorStore();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);

  const [page, setPage] = useState(1);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [customerToBlock, setCustomerToBlock] = useState(null);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = useState(false);
  const [customerToUnblock, setCustomerToUnblock] = useState(null);
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [customerToTopUp, setCustomerToTopUp] = useState(null);
  const [isAddRFIDModalOpen, setIsAddRFIDModalOpen] = useState(false);
  const [customerForRFID, setCustomerForRFID] = useState(null);
  const [form] = Form.useForm();

  const pageSize = 10;
  const {
    data: users,
    isLoading,
    isFetching,
    refetch,
  } = useFetchUsers({
    operatorId,
    search: debouncedSearch,
    offset: page,
    limit: pageSize,
    sortBy: '',
    sortOrder: 'asc',
  });
  const { mutate: toggleBlock } = useBlockUnblockUser({
    onSuccess: (data) => {
      queryClient.invalidateQueries(['users']);
      setLoadingUserId('');
      if (data?.customer?.isBlocked) {
        message.success(t('UserBlocked'));
      } else {
        message.success(t('UserUnblocked'));
      }
    },
    onError: (error) => {
      message.error(getErrorMessage(error, t('ActionFailed')));
      setLoadingUserId('');
    },
  });

  const { mutate: editCustomer, isPending } = useEditCustomer(selectedCustomer?._id, {
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setIsEditDrawerOpen(false);
      refetch();
      message.success(t('UserUpdated'));
    },
    onError: (error) => {
      message.error(getErrorMessage(error, t('ActionFailed')));
    },
  });

  const { mutate: addCredit, isPending: isAddingCredit } = useAddCustomerCredit({
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setIsTopUpModalOpen(false);
      form.resetFields();
      message.success(t('CreditAddedSuccessfully'));
    },
    onError: (error) => {
      message.error(getErrorMessage(error, t('ActionFailed')));
    },
  });

  const [resetPinModalOpen, setResetPinModalOpen] = useState(false);
  const [customerToResetPin, setCustomerToResetPin] = useState(null);
  const [showPinModalOpen, setShowPinModalOpen] = useState(false);
  const [newlyGeneratedPin, setNewlyGeneratedPin] = useState(null);

  const { mutate: getPin } = useGetCustomerPin({
    onSuccess: (data) => {
      setNewlyGeneratedPin(data?.pin);
      setShowPinModalOpen(true);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('FailedToGetPin'));
    }
  });

  const { mutate: resetPin, isPending: isResettingPin } = useResetCustomerPin({
    onSuccess: () => {
      message.success(t('PinResetSuccess'));
      setResetPinModalOpen(false);
      if (customerToResetPin?._id) {
        getPin({ id: customerToResetPin._id });
      }
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('PinResetFailed'));
    }
  });

  const handleResetPin = (customer) => {
    setCustomerToResetPin(customer);
    setResetPinModalOpen(true);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (page !== 1) {
      setPage(1);
    }
  };

  const totalUsers = users?.data?.totalCount ?? 0;

  const handleToggleBlock = (userId, isBlocked) => {
    if (isBlocked) {
      setCustomerToUnblock(userId);
      setIsUnblockModalOpen(true);
    } else {
      setCustomerToBlock(userId);
      setIsBlockModalOpen(true);
    }
  };

  const confirmBlockCustomer = () => {
    setLoadingUserId(customerToBlock);
    toggleBlock({ id: customerToBlock, status: true, operatorId });
    setIsBlockModalOpen(false);
  };

  const confirmUnblockCustomer = () => {
    setLoadingUserId(customerToUnblock);
    toggleBlock({ id: customerToUnblock, status: false, operatorId });
    setIsUnblockModalOpen(false);
  };

  const openCustomerViewDrawer = (customer) => {
    setSelectedCustomer(customer);
    setIsViewDrawerOpen(true);
  };

  const openCustomerEditDrawer = (customer) => {
    setSelectedCustomer(customer);
    setIsEditDrawerOpen(true);
  };

  const handleTopUp = (customer) => {
    setCustomerToTopUp(customer);
    setIsTopUpModalOpen(true);
  };

  const confirmTopUp = async () => {
    try {
      const values = await form.validateFields();
      addCredit({
        customerId: customerToTopUp._id,
        amount: values.amount.toString(),
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleAddRFID = (customer) => {
    setCustomerForRFID(customer);
    setIsAddRFIDModalOpen(true);
  };

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      align: 'center',
      key: 'name',
      render: (_, record) => (
        <Button type="link" onClick={() => openCustomerViewDrawer(record)}>
          {`${record.fname || '-'} ${record.lname || '-'}`}
        </Button>
      ),
    },

    {
      title: t('PhoneNumber'),
      dataIndex: 'phoneNumber',
      align: 'center',
      key: 'phoneNumber',
      render: (_, record) => `+${record.countryCode} ${record.phoneNumber}`,
    },
    {
      title: t('EmployeeId'),
      dataIndex: 'employeeId',
      key: 'employeeId',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: t('FactoryNumber'),
      dataIndex: 'factoryNumber',
      key: 'factoryNumber',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: t('TagId'),
      dataIndex: 'tagId',
      key: 'tagId',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: t('StartCharge'),
      dataIndex: 'startChargeEnabled',
      key: 'startChargeEnabled',
      align: 'center',
      render: (value) => (
        <Tag color={value === true ? 'blue' : 'orange'} variant="outlined">
          {value === true ? t('StartChargeEnabledLabel') : t('StartChargeDisabled')}
        </Tag>
      ),
    },
    {
      title: t('Status'),
      dataIndex: 'isBlocked',
      key: 'status',
      align: 'center',
      render: (value) => (
        <Tag color={value ? 'red' : 'green'} variant="outlined">
          {value ? t('Blocked') : t('Active')}
        </Tag>
      ),
    },
    {
      title: t('Action'),
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <div className="">
            <Tooltip title={t('View')}>
              <Button
                icon={<EyeOutlined />}
                type="default"
                onClick={() => openCustomerViewDrawer(record)}
              />
            </Tooltip>
          </div>
          {isEditAllowed && (
            <div className="">
              <Tooltip title={t('EditCustomer')}>
                <Button
                  icon={<EditOutlined />}
                  type="default"
                  onClick={() => openCustomerEditDrawer(record)}
                />
              </Tooltip>
            </div>
          )}
          {isEditAllowed && (
            <div className="">
              <Tooltip title={t('ResetPin')}>
                <Button
                  icon={<SyncOutlined />}
                  type="default"
                  onClick={() => handleResetPin(record)}
                />
              </Tooltip>
            </div>
          )}
          {isEditAllowed && (
            <div className="">
              <Tooltip title={t('AddRFID')}>
                <Button
                  icon={<IdcardOutlined />}
                  type="default"
                  onClick={() => handleAddRFID(record)}
                />
              </Tooltip>
            </div>
          )}
          {showWallet && isEditAllowed && (
            <div className="">
              <Tooltip title={t('topUpBalance')}>
                <Button
                  icon={<WalletOutlined />}
                  type="default"
                  onClick={() => handleTopUp(record)}
                />
              </Tooltip>
            </div>
          )}
          {isEditAllowed && (
            <div className="">
              {record.isBlocked ? (
                <Tooltip title={t('Unblock')}>
                  <Button
                    icon={<UnlockOutlined />}
                    loading={loadingUserId === record._id}
                    onClick={() => handleToggleBlock(record._id, true)}
                  />
                </Tooltip>
              ) : (
                <Tooltip title={t('Block')}>
                  <Button
                    icon={<LockOutlined />}
                    loading={loadingUserId === record._id}
                    onClick={() => handleToggleBlock(record._id, false)}
                  />
                </Tooltip>
              )}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {!operatorId ? (
        <WarningMessage />
      ) : (
        <>
          <div className="flex justify-between items-center gap-4">
            <Input
              allowClear
              placeholder={t('SearchPlaceholder')}
              prefix={<SearchOutlined />}
              style={{ width: '300px' }}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {isEditAllowed && (
              <Button type="primary" onClick={() => setIsDrawerOpen(true)}>
                {t('AddCustomer')}
              </Button>
            )}
          </div>
          {isLoading ? (
            <div className="flex justify-center">
              <Spin tip={t('Loading')} />
            </div>
          ) : (
            <Table
              variant="outlined"
              columns={columns}
              dataSource={users?.data?.paginatedData || []}
              loading={isFetching}
              size="small"
              tableLayout="fixed"
              scroll={{ x: 1200, y: 60 * 8 }}
              pagination={{
                current: page,
                pageSize,
                size: 'default',
                showSizeChanger: false,
                total: totalUsers,
                onChange: (p) => setPage(p),
                position: ['bottomCenter'],
              }}
              rowKey={(record) => record._id}
            />
          )}
      {/* Modal for Blocking Resident */}
      <Modal
        confirmLoading={loadingUserId === customerToBlock}
        title={t('BlockCustomerTitle')}
        open={isBlockModalOpen}
        onCancel={() => setIsBlockModalOpen(false)}
        onOk={confirmBlockCustomer}
      >
        <p>{t('BlockCustomerMessage')}</p>
      </Modal>
      {/* Modal for Unblocking Resident */}
      <Modal
        confirmLoading={loadingUserId === customerToUnblock}
        title={t('UnblockCustomerTitle')}
        open={isUnblockModalOpen}
        onCancel={() => setIsUnblockModalOpen(false)}
        onOk={confirmUnblockCustomer}
      >
        <p>{t('UnblockCustomerMessage')}</p>
      </Modal>
      {/* TopUp Modal */}
      <Modal
        title={t('TopUpBalance')}
        open={isTopUpModalOpen}
        onCancel={() => {
          setIsTopUpModalOpen(false);
          form.resetFields();
        }}
        onOk={confirmTopUp}
        okButtonProps={{
          disabled: isAddingCredit,
          loading: isAddingCredit,
        }}
        confirmLoading={isAddingCredit}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="amount"
            label={t('Amount')}
            rules={[
              { required: true, message: t('PleaseEnterAmount') },
              { type: 'number', min: 0, message: t('AmountMustBePositive') }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t('EnterAmount')}
              prefix="₹"
            />
          </Form.Item>
        </Form>
      </Modal>
      {/* Reset PIN Modal */}
      <Modal
        title={t('ResetPinTitle')}
        open={resetPinModalOpen}
        onCancel={() => {
          setResetPinModalOpen(false);
          setCustomerToResetPin(null);
        }}
        onOk={() => {
          resetPin({ id: customerToResetPin?._id });
        }}
        confirmLoading={isResettingPin}
      >
        <Typography.Text>{t('ResetPinConfirmation')}</Typography.Text>
      </Modal>
      {/* Add RFID Modal */}
      {isAddRFIDModalOpen && (
        <AddRFIDModalDirectlyUser
          open={isAddRFIDModalOpen}
          setClose={() => {
            setIsAddRFIDModalOpen(false);
            setCustomerForRFID(null);
          }}
          assignedTo={customerForRFID}
        />
      )}
      <Modal
        title={t('ClientPin')}
        open={showPinModalOpen}
        centered
        onCancel={() => {
          setShowPinModalOpen(false);
          setNewlyGeneratedPin(null);
          setCustomerToResetPin(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setShowPinModalOpen(false);
            setNewlyGeneratedPin(null);
            setCustomerToResetPin(null);
          }}>
            {t('Close')}
          </Button>
        ]}
      >
        <div className="flex flex-col items-center justify-center gap-6 py-4">
          <div className="text-center space-y-1">
            <Typography.Text type="secondary">
              {t('NewPinGeneratedFor')}
            </Typography.Text>
            <div className="font-semibold text-lg">
              {customerToResetPin?.fname} {customerToResetPin?.lname}
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 w-full">
            <div className="bg-gray-50 h-12 min-w-[200px] px-4 rounded-lg border border-gray-200 flex items-center justify-center">
              <span className="font-mono text-xl font-bold text-slate-700 tracking-[0.2em]">
                {newlyGeneratedPin}
              </span>
              <Tooltip title={t('Copy')}>
                <CopyOutlined className="text-xl cursor-pointer ml-2 hover:text-primary" onClick={() => {
                  navigator.clipboard.writeText(newlyGeneratedPin);
                  message.success(t('PinCopied'));
                }} />
              </Tooltip>
            </div>

          </div>

          <Typography.Text type="secondary" className="text-center text-xs">
            {t('CopyHelp')}
          </Typography.Text>
        </div>
      </Modal>
      {/* Drawer for Adding a New Resident */}
      {isDrawerOpen && (
        <CustomerDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
      {/* Drawer for Viewing Resident Details & Transactions */}
      {isViewDrawerOpen && selectedCustomer && (
        <CustomerDrawerView
          open={isViewDrawerOpen}
          onClose={() => setIsViewDrawerOpen(false)}
          id={selectedCustomer?._id ?? selectedCustomer?.id}
          operatorId={operatorId}
        />
      )}
      {/* Drawer for Editing Resident Details */}
      {isEditDrawerOpen && selectedCustomer && (
        <EditCustomerDrawer
          open={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          customer={selectedCustomer}
          isPending={isPending}
          onSave={editCustomer}
        />
      )}
        </>
      )}
    </div>
  );
};

export default CustomersListPage;
